import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdModal, CmdModalFooterButton, CmdTextarea } from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import Label from '../../Forms/Fields/Label';
import usePageWithForm from '../../../hooks/usePageWithForm';
import {
  IAddComment,
  ICheckedTableItems,
  IFilterTripsOverview,
  ITripOverviewParking,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewInfo,
} from '../interfaces';
import { IS_COMMENT } from '../constants';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  commentAtom,
  filterForRequestAtom,
  isActiveAdvanceFilterAtom,
  rideItemAtom,
} from '../TripsOverviewState';
import { changeComment, convertTimeToSeconds, handleFilterKey } from '../TripsOverviewService';
import { transformErrors } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';
import { GDPR_VALUE } from '../../CmdTable/constants';

interface IAddCommentProps {
  closeCallback: () => void;
  type: string;
  data: ITripsOverViewRecord | null;
  updateTable: () => void;
}

export const IS_COMMENT_PARKING = 'comment-parking';

function AddComment(props: IAddCommentProps): JSX.Element {
  usePageWithForm();
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const { formatMessage: f } = useIntl();
  const setRideItem = useSetRecoilState(rideItemAtom(props.data ? String(props.data.id) : ''));
  const setComment = useSetRecoilState(commentAtom);
  const [isLongStart, setIsLongStart] = React.useState<boolean>(false);
  const [isLongEnd, setIsLongEnd] = React.useState<boolean>(false);
  const [isLongVehicle, setIsLongVehicle] = React.useState<boolean>(false);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const validateFields = (values: IAddComment) => {
    let formErrors = {};
    const validation: IValidationApiResponse = {
      comment: [
        { param: 'required', value: true, message: f({ id: 'form.required' }) },
        { param: 'maxLength', value: 200, message: f({ id: 'form.numberMax200' }) },
      ],
    };
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ comment: string }>({
    mode: 'onChange',
    defaultValues: {
      comment: '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = async (values: IAddComment) => {
    trackEvent(['ride book', 'click', 'Ride book add comment']);
    setIsLoaderActive(true);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    const res = await changeComment(values, props.data, checkedTableItems, props.type, filter);
    if (res) {
      props.closeCallback();
      if (isActiveAdvanceFilter.comment) {
        props.updateTable();
      } else if (props.data) {
        setRideItem((oldState: ITripsOverViewRecord | null) => {
          if (oldState) {
            const newState = { ...oldState };
            const infoData: ITripsOverviewInfo = { ...oldState.info };
            infoData[props.type === IS_COMMENT_PARKING ? 'parkingComment' : 'rideComment'] =
              values.comment;
            if (props.type === IS_COMMENT_PARKING && oldState.parking) {
              const parkingData: ITripOverviewParking = { ...oldState.parking };
              parkingData.summary = {
                ...parkingData.summary,
                comment: parkingData.summary?.comment ? Number(parkingData.summary.comment) + 1 : 1,
              };
              newState.parking = { ...parkingData };
            } else {
              infoData.summary = {
                ...infoData.summary,
                comment: infoData.summary?.comment ? Number(infoData.summary.comment) + 1 : 1,
              };
            }
            newState.info = { ...infoData };
            return newState;
          }
          return null;
        });
      } else {
        setComment({ text: values.comment, type: props.type });
      }
    }

    setIsLoaderActive(false);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'form.cancel' }),
      tooltip: f({ id: 'rides.modal.comment.cancel.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'addcomment-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'tripsOverview.modal.toAddComment' }),
      tooltip:
        props.type === 'comment'
          ? f({ id: 'rides.modal.comment.submit.tooltip' })
          : f({ id: 'rides.modal.parkingComment.submit.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  React.useEffect(() => {
    if (props.data) {
      const startId = document.getElementById(`ride-start-${props.data.timestamp.start}`);
      if (startId && !isLongStart && startId.clientHeight > 19) {
        setIsLongStart(true);
      }
      const endId = document.getElementById(`ride-end-${props.data.timestamp.end}`);
      if (endId && !isLongEnd && endId.clientHeight > 19) {
        setIsLongEnd(true);
      }
      const vehicleId = document.getElementById(`vehicle-${props.data.vehicle.id}`);
      if (vehicleId && !isLongVehicle && vehicleId.clientHeight > 19) {
        setIsLongVehicle(true);
      }
    }
  }, [isLongEnd, isLongStart, isLongVehicle, props.data]);

  const getStartAddress = () => {
    const anonymized = f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
    if (props.type === IS_COMMENT) {
      return !props.data?.address.startLink ? anonymized : props.data?.address.start;
    }
    if (props.type !== IS_COMMENT) {
      return !props.data?.parking?.address.startLink
        ? anonymized
        : props.data?.parking?.address.start;
    }
    return '';
  };

  const getStartTimestamp = () => {
    if (props.data && props.data.timestamp.start && props.type === IS_COMMENT) {
      return dayjs(props.data.timestamp.start).format('DD.MM.YYYY HH:mm:ss');
    }
    if (
      props.data &&
      props.data.parking &&
      props.data.parking.timestamp.start &&
      props.type !== IS_COMMENT
    ) {
      return dayjs(props.data.parking.timestamp.start).format('DD.MM.YYYY HH:mm:ss');
    }
    return '';
  };

  const getEndAddress = () => {
    if (props.type === IS_COMMENT && props.data?.address.endLink) {
      return props.data.address.end;
    }
    if (props.type !== IS_COMMENT && props.data?.parking?.address.endLink) {
      return props.data.parking.address.end;
    }
    return f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
  };

  const getIsTimestampAnonymized = () => {
    if (props.type === IS_COMMENT && props.data?.timestamp.end === GDPR_VALUE) {
      return true;
    }
    if (props.type !== IS_COMMENT && props.data?.parking?.timestamp.end === GDPR_VALUE) {
      return true;
    }
    return false;
  };

  const getEndValue = () => {
    const address = getEndAddress();
    if (getIsTimestampAnonymized()) {
      return (
        <>
          <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" /> - {address}
        </>
      );
    }
    if (props.type === IS_COMMENT) {
      if (props.data?.timestamp.end) {
        return `${dayjs(props.data?.timestamp.end).format('DD.MM.YYYY HH:mm:ss')} - ${address}`;
      }
    } else if (props.data?.parking?.timestamp.end) {
      return `${dayjs(props.data?.parking?.timestamp.end).format(
        'DD.MM.YYYY HH:mm:ss'
      )} - ${address}`;
    }
    return '';
  };

  const getModalTitle = () => {
    if (props.type === IS_COMMENT) {
      return props.data
        ? f({ id: 'rides.modal.comment.submit.tooltip' })
        : f({ id: 'rides.modal.comment.title.bulk' });
    }
    return props.data
      ? f({ id: 'rides.modal.parkingComment.submit.tooltip' })
      : f({ id: 'rides.modal.parkingComment.title.bulk' });
  };

  return (
    <CmdModal
      id="comment-form-modal"
      title={getModalTitle()}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {props.data && (
        <div style={{ marginBottom: '24px' }}>
          <Label
            title={f({ id: 'tripsOverview.modal.vehicle' })}
            value={`${props.data.vehicle.licenceNumber} - ${props.data.vehicle.name}`}
            isLong={isLongVehicle}
            name={`vehicle-${props.data.vehicle.id}`}
            newStyle
          />
          {props.data && (
            <Label
              title={
                props.type === IS_COMMENT
                  ? f({ id: 'tripsOverview.modal.rideStart' })
                  : f({ id: 'tripsOverview.modal.parkingStart' })
              }
              value={`${getStartTimestamp()} - ${getStartAddress()}`}
              newStyle
              isLong={isLongStart}
              name={`ride-start-${props.data.timestamp.start}`}
            />
          )}
          {props.data.timestamp.end && (
            <Label
              title={
                props.type === IS_COMMENT
                  ? f({ id: 'tripsOverview.modal.rideEnd' })
                  : f({ id: 'tripsOverview.modal.parkingEnd' })
              }
              value={getEndValue()}
              newStyle
              isLong={isLongEnd}
              name={`ride-end-${props.data.timestamp.end}`}
            />
          )}
        </div>
      )}
      <Controller
        name="comment"
        control={control}
        rules={{
          required: errors.comment ? errors.comment.message : undefined,
        }}
        render={({ field }) => (
          <CmdTextarea
            {...field}
            id="comment"
            name="comment"
            label={f({ id: 'tripsOverview.modal.comment' })}
            required={true}
            value={watch('comment')}
            rows={8}
            setValue={(value: string) => {
              setValue('comment', value);
              trigger('comment');
            }}
            error={errors.comment?.message}
            touched={true}
          />
        )}
      />
    </CmdModal>
  );
}

export default AddComment;
